import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, unref as _unref, createVNode as _createVNode, withKeys as _withKeys } from "vue";
const _hoisted_1 = {
    key: 0,
    role: "presentation",
    "data-testid": "global-search-loading",
    class: "px-2 py-3 text-(length:--size-m) text-(--colors-text-body-subtle)"
};
const _hoisted_2 = {
    key: 1,
    role: "presentation",
    "data-testid": "global-search-error",
    class: "px-2 py-3 text-(length:--size-m) text-(--colors-text-body-subtle)"
};
const _hoisted_3 = {
    key: 2,
    role: "presentation",
    "data-testid": "global-search-empty",
    class: "px-2 py-3 text-(length:--size-m) text-(--colors-text-body-subtle)"
};
const _hoisted_4 = {
    role: "presentation",
    class: "w-full px-2 pt-0.5 pb-1 leading-5 font-medium text-(length:--size-sm) text-(--colors-text-body-subtle)"
};
const _hoisted_5 = ["onClick"];
const _hoisted_6 = { class: "flex min-w-0 flex-col" };
const _hoisted_7 = { class: "truncate text-(length:--size-m) font-medium text-(--colors-text-black-default) dark:text-(--colors-text-white-default)" };
const _hoisted_8 = {
    key: 0,
    class: "truncate text-(length:--size-sm) text-(--colors-text-body-subtle)"
};
const _hoisted_9 = {
    role: "presentation",
    class: "w-full px-2 pt-0.5 pb-1 leading-5 font-medium text-(length:--size-sm) text-(--colors-text-body-subtle)"
};
const _hoisted_10 = ["onClick"];
const _hoisted_11 = { class: "flex min-w-0 flex-col" };
const _hoisted_12 = { class: "truncate text-(length:--size-m) font-medium text-(--colors-text-black-default) dark:text-(--colors-text-white-default)" };
const _hoisted_13 = {
    key: 0,
    class: "truncate text-(length:--size-sm) text-(--colors-text-body-subtle)"
};
import { computed } from "vue";
import { Avatar } from "@eudonet/opal";
/** Maximum rows rendered per group in the quick-search dropdown. */
const MAX_PER_GROUP = 5;
export default /*@__PURE__*/ _defineComponent({
    __name: 'GlobalSearchResults',
    props: {
        results: {},
        isLoading: { type: Boolean },
        error: { type: Boolean },
        query: {}
    },
    emits: ["select", "seeAll", "close"],
    setup(__props) {
        const props = __props;
        const isEmpty = computed(() => props.results.contacts.length === 0 && props.results.organisations.length === 0);
        const visibleContacts = computed(() => props.results.contacts.slice(0, MAX_PER_GROUP));
        const visibleOrganisations = computed(() => props.results.organisations.slice(0, MAX_PER_GROUP));
        // The "see all" row shows only when the list we actually received is larger than
        // the preview — never on the authoritative total alone, so an over-reporting
        // backend (total > items it returned) can't offer to open more rows than exist.
        // With pageSize (20) well above MAX_PER_GROUP (5), a truncated list reliably
        // signals real overflow.
        const hasMoreContacts = computed(() => props.results.contacts.length > visibleContacts.value.length);
        const hasMoreOrganisations = computed(() => props.results.organisations.length > visibleOrganisations.value.length);
        function contactName(contact) {
            return `${contact.firstName} ${contact.lastName}`.trim() || contact.id;
        }
        function contactSubtitle(contact) {
            return contact.organisationName ?? contact.primaryEmail ?? null;
        }
        function organisationSubtitle(organisation) {
            return organisation.tradeName ?? organisation.acronym ?? null;
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                role: "menu",
                "data-testid": "global-search-results",
                class: "bg-white-50-opacity-bold border-border-base-soft box-border flex max-h-[min(420px,calc(100vh-96px))] w-full flex-col items-stretch gap-1.5 overflow-y-auto rounded-(--border-border-radius-rounded-l) border border-solid p-2 shadow-(--blur-shadow-m) [backdrop-filter:blur(4px)] dark:border-(--colors-border-base) dark:bg-(--colors-background-black-default) dark:shadow-(--blur-shadow-m-dark)",
                onKeydown: _cache[2] || (_cache[2] = _withKeys(($event) => (_ctx.$emit('close')), ["escape"]))
            }, [
                (__props.isLoading)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_1, _toDisplayString(_ctx.$t("navbar.search.loading")), 1 /* TEXT */))
                    : (__props.error)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(_ctx.$t("navbar.search.error")), 1 /* TEXT */))
                        : (isEmpty.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_ctx.$t("navbar.search.noResults")), 1 /* TEXT */))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                                (__props.results.contacts.length > 0)
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                        _createElementVNode("div", _hoisted_4, _toDisplayString(_ctx.$t("navbar.search.contacts")), 1 /* TEXT */),
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleContacts.value, (contact) => {
                                            return (_openBlock(), _createElementBlock("button", {
                                                key: `contact-${contact.id}`,
                                                type: "button",
                                                role: "menuitem",
                                                "data-testid": "global-search-contact",
                                                class: "hover:bg-background-hover focus-visible:ring-royalblue inline-flex w-full cursor-pointer items-center gap-2 rounded-(--border-border-radius-rounded-sm) p-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 dark:hover:bg-white/8",
                                                onClick: ($event) => (_ctx.$emit('select', { kind: 'contact', id: contact.id }))
                                            }, [
                                                _createVNode(_unref(Avatar), {
                                                    presentational: "",
                                                    as: "span",
                                                    name: contactName(contact),
                                                    img: contact.avatarUrl ?? undefined
                                                }, null, 8 /* PROPS */, ["name", "img"]),
                                                _createElementVNode("span", _hoisted_6, [
                                                    _createElementVNode("span", _hoisted_7, _toDisplayString(contactName(contact)), 1 /* TEXT */),
                                                    (contactSubtitle(contact))
                                                        ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(contactSubtitle(contact)), 1 /* TEXT */))
                                                        : _createCommentVNode("v-if", true)
                                                ])
                                            ], 8 /* PROPS */, _hoisted_5));
                                        }), 128 /* KEYED_FRAGMENT */)),
                                        (hasMoreContacts.value)
                                            ? (_openBlock(), _createElementBlock("button", {
                                                key: 0,
                                                type: "button",
                                                role: "menuitem",
                                                "data-testid": "global-search-more-contacts",
                                                class: "hover:bg-background-hover focus-visible:ring-royalblue w-full cursor-pointer rounded-(--border-border-radius-rounded-sm) px-2 py-1 text-left font-medium text-(length:--size-sm) text-(--colors-text-link) transition-colors focus-visible:outline-none focus-visible:ring-2 dark:hover:bg-white/8",
                                                onClick: _cache[0] || (_cache[0] = ($event) => (_ctx.$emit('seeAll', 'contact')))
                                            }, _toDisplayString(_ctx.$t("navbar.search.seeAllContacts", { count: __props.results.totalContacts })), 1 /* TEXT */))
                                            : _createCommentVNode("v-if", true)
                                    ], 64 /* STABLE_FRAGMENT */))
                                    : _createCommentVNode("v-if", true),
                                (__props.results.organisations.length > 0)
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                        _createElementVNode("div", _hoisted_9, _toDisplayString(_ctx.$t("navbar.search.organisations")), 1 /* TEXT */),
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleOrganisations.value, (organisation) => {
                                            return (_openBlock(), _createElementBlock("button", {
                                                key: `organisation-${organisation.id}`,
                                                type: "button",
                                                role: "menuitem",
                                                "data-testid": "global-search-organisation",
                                                class: "hover:bg-background-hover focus-visible:ring-royalblue inline-flex w-full cursor-pointer items-center gap-2 rounded-(--border-border-radius-rounded-sm) p-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 dark:hover:bg-white/8",
                                                onClick: ($event) => (_ctx.$emit('select', { kind: 'organisation', id: organisation.id }))
                                            }, [
                                                _createVNode(_unref(Avatar), {
                                                    presentational: "",
                                                    as: "span",
                                                    entity: "organisation",
                                                    name: organisation.legalName,
                                                    img: organisation.logoUrl ?? undefined
                                                }, null, 8 /* PROPS */, ["name", "img"]),
                                                _createElementVNode("span", _hoisted_11, [
                                                    _createElementVNode("span", _hoisted_12, _toDisplayString(organisation.legalName), 1 /* TEXT */),
                                                    (organisationSubtitle(organisation))
                                                        ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(organisationSubtitle(organisation)), 1 /* TEXT */))
                                                        : _createCommentVNode("v-if", true)
                                                ])
                                            ], 8 /* PROPS */, _hoisted_10));
                                        }), 128 /* KEYED_FRAGMENT */)),
                                        (hasMoreOrganisations.value)
                                            ? (_openBlock(), _createElementBlock("button", {
                                                key: 0,
                                                type: "button",
                                                role: "menuitem",
                                                "data-testid": "global-search-more-organisations",
                                                class: "hover:bg-background-hover focus-visible:ring-royalblue w-full cursor-pointer rounded-(--border-border-radius-rounded-sm) px-2 py-1 text-left font-medium text-(length:--size-sm) text-(--colors-text-link) transition-colors focus-visible:outline-none focus-visible:ring-2 dark:hover:bg-white/8",
                                                onClick: _cache[1] || (_cache[1] = ($event) => (_ctx.$emit('seeAll', 'organisation')))
                                            }, _toDisplayString(_ctx.$t("navbar.search.seeAllOrganisations", { count: __props.results.totalOrganisations })), 1 /* TEXT */))
                                            : _createCommentVNode("v-if", true)
                                    ], 64 /* STABLE_FRAGMENT */))
                                    : _createCommentVNode("v-if", true)
                            ], 64 /* STABLE_FRAGMENT */))
            ], 32 /* NEED_HYDRATION */));
        };
    }
});
